import * as React from "react"
import ProgressOr from "../../components/ProgressOr";
import {Grid, Tab, IconButton, Box} from "@material-ui/core";
import {useHistory, useParams} from "react-router-dom";
import {useEffect, useRef, useState} from "react";
import Rest, {inDevMode} from "../../helpers/Rest";
import {DateFormat} from "../../helpers/DateFormat";
import DataTable from "../../components/DataTable/DataTable";
import {useCustomers} from "../../context/CustomersContext";
import DeviceTable from "./DeviceTable";
import {useDevices} from "../../context/DevicesContext";
import DeviceFilters from "./DeviceFilters";
import Widget from "../../components/BetterWidget";
import {columnOffsets} from "./Config";
import {StatusChip, TicketStatusChip} from "./StatusChip";
import {TabContext, TabList, TabPanel} from "@material-ui/lab";
import { CloudDownload } from "@material-ui/icons";

const {ID, MONITORED, SITE, CUSTOMER} = columnOffsets

const FIELDS = (() => {
    const common = ["device_code", "device_type", "device_name", "customer_name", "site_name", "hw_version", "sw_version"]
    return {
        faults: common.concat(["test_status", "error_messages", ["fault_time", "fault_cleared_time"], "ticket_id", "ticket_status"]),
        offline: common.concat([["last_reported", "fault_cleared_time"], ["last_reported_age", "fault_age"], "ticket_id", "ticket_status"])
    }
})()

function formatTime(ts){
    if (!ts){
        return 'never'
    }
    if (ts < 120){
        return `${ts.toFixed(0)} seconds`
    }
    ts /= 60
    if (ts < 120){
        return `${ts.toFixed(0)} minutes`
    }
    ts /= 60
    if (ts < 48){
        return `${ts.toFixed(0)} hours`
    }
    return `${(ts / 24).toFixed(0)} days`
}

function formatDate(date){
    return date && (new DateFormat(date)).datetime() || "never"
}

function dateColumn(name){
    return {
        name: name,
        options: {
            customBodyRender: formatDate
        }
    }
}

const zendDeskHost = `https://${inDevMode() ? "d3v-testntel.zendesk.com" : "sonasafe.zendesk.com"}/agent/tickets/`

const deviceIdLookup = {}

function compareTicketStatus(a, b){
    a = a.rowData[11]
    b = b.rowData[11]
    return (a?.status || "").localeCompare(b?.status || "")
}

const FIELD_LABELS = {
    device_code: {
        name: "Device ID",
        options: {
            customBodyRender: (deviceCode) => {
                const deviceId = deviceIdLookup[deviceCode]
                return deviceId
                    ? <a href={`#/app/edit-device/${deviceId}`} target="device_code" onClick={e => e.stopPropagation()}>{deviceCode}</a>
                    : deviceCode
            }
        }
    },
    device_type: "Type",
    device_name: "Name",
    customer_name: "Customer",
    site_name: "Site",
    hw_version: "Hw ver",
    sw_version: "Sw ver",
    test_status: {
        name: "Tested",
        options: {
            customBodyRender: (tested) => {
                return tested === 'untested' ? <span style={{color: '#c00'}}>No</span> : "Yes"
            }
        }
    },
    fault_time: dateColumn("Fault Reported"),
    fault_cleared_time: dateColumn("Fault Resolved"),
    latest_state: {
        name: "Status",
        options: {
            customBodyRender: (state) => <StatusChip status={state} />
        }
    },
    ticket_id: {
        name: "Ticket ID",
        options: {
            customBodyRender: (ticketId) => {
                return <a href={zendDeskHost + ticketId} target="zen-desk" onClick={e => e.stopPropagation()}>{ticketId}</a>
            }
        }
    },
    ticket_status: {
        name: "Ticket Status",
        options: {
            customBodyRender: (value) => {
                return value ? <TicketStatusChip status={value.status}>{value.label}</TicketStatusChip> : null
            },
            sortCompare: (direction) => {
                return direction === "asc" ? (a, b) => compareTicketStatus(a, b) : (a, b) => compareTicketStatus(b, a)
            }
        }
    },
    last_reported: dateColumn("Last Reported"),
    last_reported_age: {
        name: "Offline For",
        options: {
            customBodyRender: offlineFor => formatTime(offlineFor)
        }
    },
    fault_age: {
        name: "Offline For",
        options: {
            customBodyRender: offlineFor => formatTime(offlineFor)
        }
    },
    error_messages: {
        name: "Messages"
    }
}

const COLUMNS = Object.keys(FIELDS).reduce((columns, key) => {
    const items = FIELDS[key]
    columns[key] = items.map(field => (field instanceof Array) ? field.map(item => FIELD_LABELS[item]) : FIELD_LABELS[field])
    return columns
}, {})

const HEADINGS = {
    faults: "Faulty Devices",
    offline: "Offline Devices",
    unmonitored: "Unmonitored Devices"
}

const SORT = {
    faults: [FIELD_LABELS.fault_time.name, FIELD_LABELS.fault_cleared_time.name],
    offline: [FIELD_LABELS.last_reported.name, FIELD_LABELS.fault_cleared_time.name]
}

function applyOffset(ary, offset){
    return ary.map(item => (item instanceof Array) && item[offset] || item)
}

function applyFilter(row, fields){
    const len = fields.length
    const out = new Array(len)
    for (let i = 0; i < len; ++i){
        const field = fields[i]
        out[i] = (field === "ticket_status") ? {
            status: row.ticket_status,
            label: row.ticket_status_label
        } : row[field]
    }
    return out
}

function Unmonitored({customerId, siteId}){
    const customersContext = useCustomers()
    const devicesContext = useDevices()
    const history = useHistory()
    const [customers, setCustomers] = useState(null)
    const [devices, setDevices] = useState(null)
    const [filtered, setFiltered] = useState(null)
    const loading = !(customers && filtered)

    useEffect(() => {
        if (customersContext){
            customersContext.fetchCustomers().then(setCustomers)
        }
    }, [customersContext])

    useEffect(() => {
        if (devicesContext){
            devicesContext.fetchDevices().then(devices => setDevices(
                devices.list.filter(device => !device[MONITORED]))
            )
        }
    }, [devicesContext]);

    useEffect(() => {
        if (devices){
            if (!(siteId || customerId)){
                setFiltered(devices)
                return
            }
            setFiltered(devices.filter(device => (!customerId || device[CUSTOMER] === customerId) &&
                (!siteId || device[SITE] === siteId)
            ))
        }
    }, [devices, customerId, siteId]);

    return <ProgressOr showProgress={loading} progressMessage="Loading devices">
        {loading ? null : <DeviceTable
            customers={customers}
            devices={filtered}
            onEdit={(device) => {
                history.push({
                    pathname: `/app/edit-device/${device[ID]}`,
                    state: device
                })
            }}
            onDelete={() => {}}
        />}
    </ProgressOr>
}

function Monitored({filter, customerId, siteId}){
    const history = useHistory()
    const [open, setOpen] = useState(null)
    const [closed, setClosed] = useState(null)
    const [devices, setDevices] = useState(null)
    const idLookup = useRef({})
    const [openTab, setOpenTab] = useState('0')
    const [columns, setColumns] = useState(null)

    const faultCleared = openTab === '1'

    async function getDevices(){
        const params = new URLSearchParams()
        if (siteId){
            params.set("site_id", siteId)
        }
        else if (customerId){
            params.set("customer_id", customerId)
        }
        params.set("offline", (filter === "offline").toString())
        params.set("fault_cleared", faultCleared.toString())
        const fields = applyOffset(FIELDS[filter], faultCleared && 1 || 0)
        const lookup = idLookup.current
        return (await Rest.get(`device/fault/list?${params}`)).map(row => {
            lookup[row.device_code] = row.device_id
            deviceIdLookup[row.device_code] = row.device_id
            return applyFilter(row, fields)
        })
    }

    useEffect(() => {
        setOpen(null)
        setClosed(null)
    }, [filter, customerId, siteId]);

    useEffect(() => {
        if (faultCleared){
            if (!closed){
                getDevices().then(setClosed)
            }
        }
        else if (!open){
            getDevices().then(setOpen)
        }
    }, [filter, customerId, siteId, faultCleared, open, closed])

    useEffect(() => {
        setDevices([open, closed]);
        setColumns(COLUMNS[filter])
    }, [open, closed])

    return <ProgressOr showProgress={!devices} progressMessage="Loading devices">
        <Widget>
            <TabContext value={openTab}>
                <TabList
                    onChange={(e, val) => setOpenTab(val)}
                    textColor="primary"
                    indicatorColor="primary"
                    variant="fullWidth"
                    style={{alignSelf: "self-start"}}
                >
                    <Tab value='0' label="Unresolved"/>
                    <Tab value='1' label="Closed" />
                </TabList>
                {devices && devices.map((devices, i) => <TabPanel key={i} value={i.toString()} style={{position:"relative"}}>
                    <IconButton onClick={() => Rest.getCsv('device/fault/csv', `faults-${new Date().toISOString()}.csv`)} style={{position:"absolute", right: "100px", top: "32px", zIndex: 1}}><CloudDownload/></IconButton>
                    <DataTable
                        data={devices || []}
                        columns={columns && applyOffset(columns, i) || []}
                        //
                        onEdit={(row) => history.push({
                            pathname: `/app/edit-device/${idLookup.current[row[0]]}`
                        })}
                        onDelete={() => {}}
                        sortOrder={{name: SORT[filter][i], direction: "desc"}}
                    />
                </TabPanel>) || null}
            </TabContext>
        </Widget>
    </ProgressOr>
}

export default function(){
    const {filter} = useParams()
    const customerContext = useCustomers()
    const [customers, setCustomers] = useState(null)
    const [customerId, setCustomerId] = useState(0)
    const [siteId, setSiteId] = useState(0)

    useEffect(() => {
        customerContext.fetchCustomers().then(setCustomers)
    }, [customerContext])

    return <Grid container spacing={3}>
        <Grid item xs={12}>
            <DeviceFilters
                customers={customers}
                customerId={customerId}
                siteId={siteId}
                onChangeCustomerId={setCustomerId}
                onChangeSiteId={setSiteId}
                heading={HEADINGS[filter]}
            />
        </Grid>
        <Grid item xs={12}>
            {filter === "unmonitored" ? <Unmonitored
                customerId={customerId}
                siteId={siteId}
            /> : <Monitored
                filter={filter}
                customerId={customerId}
                siteId={siteId}
            />}
        </Grid>
    </Grid>
}
